import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import messageService from '../../services/messageService';

// Async thunks
// Map frontend tab/type to backend query type
const getBackendType = (tab) => {
  switch (tab) {
    case 'requests': return 'request';
    case 'coach': return 'coach';
    case 'archive': return 'archive';
    default: return undefined; // 'all'
  }
};

export const fetchConversations = createAsyncThunk(
  'messaging/fetchConversations',
  async (type, { rejectWithValue }) => {
    try {
      const res = await messageService.getMyConversations(getBackendType(type));
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch conversations');
    }
  }
);

export const fetchMessages = createAsyncThunk(
  'messaging/fetchMessages',
  async ({ conversationId, page, limit }, { rejectWithValue }) => {
    try {
      const res = await messageService.getMessages(conversationId, page, limit);
      return { conversationId, messages: res.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch messages');
    }
  }
);

export const sendMessage = createAsyncThunk(
  'messaging/sendMessage',
  async ({ conversationId, text, files }, { rejectWithValue }) => {
    try {
      let data;
      if (files && files.length > 0) {
        data = new FormData();
        data.append('conversationId', conversationId);
        if (text) data.append('text', text);
        files.forEach((file) => {
          data.append('file', file);
        });
      } else {
        data = { conversationId, text };
      }
      const res = await messageService.sendMessage(data);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send message');
    }
  }
);

export const markAsRead = createAsyncThunk(
  'messaging/markAsRead',
  async (conversationId, { rejectWithValue }) => {
    try {
      await messageService.markAsRead(conversationId);
      return conversationId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark as read');
    }
  }
);

export const deleteConversations = createAsyncThunk(
  'messaging/deleteConversations',
  async (conversationIds, { rejectWithValue }) => {
    try {
      await messageService.deleteConversations(conversationIds);
      return conversationIds;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete conversations');
    }
  }
);

export const deleteMessage = createAsyncThunk(
  'messaging/deleteMessage',
  async (messageId, { rejectWithValue }) => {
    try {
      await messageService.deleteMessage(messageId);
      return messageId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete message');
    }
  }
);

export const archiveConversations = createAsyncThunk(
  'messaging/archiveConversations',
  async (conversationIds, { rejectWithValue }) => {
    try {
      await messageService.archiveConversations(conversationIds);
      return conversationIds;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to archive conversations');
    }
  }
);

export const unarchiveConversations = createAsyncThunk(
  'messaging/unarchiveConversations',
  async (conversationIds, { rejectWithValue }) => {
    try {
      await messageService.unarchiveConversations(conversationIds);
      return conversationIds;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to unarchive conversations');
    }
  }
);

export const startConversation = createAsyncThunk(
  'messaging/startConversation',
  async ({ playerId, text, files }, { rejectWithValue }) => {
    try {
      let data;
      if (files && files.length > 0) {
        data = new FormData();
        data.append('playerId', playerId);
        if (text) data.append('text', text);
        files.forEach((file) => {
          data.append('file', file);
        });
      } else {
        data = { playerId, text };
      }
      const res = await messageService.startConversation(data);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to start conversation');
    }
  }
);

const initialState = {
  conversations: [],
  messages: [],
  selectedChat: null,
  activeTab: 'all',
  loadingConversations: false,
  loadingMessages: false,
  sendingMessage: false,
  startingConversation: false,
  newConversationPlayer: null, // Player selected for new conversation
  error: null,
  onlineUsers: [],       // array of userId strings
  typingUsers: {},        // { [chatId]: userId }
};

const messagingSlice = createSlice({
  name: 'messaging',
  initialState,
  reducers: {
    setSelectedChat: (state, action) => {
      state.selectedChat = action.payload;
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    clearMessages: (state) => {
      state.messages = [];
    },
    // Socket event handlers
    addNewMessage: (state, action) => {
      const { conversationId, message } = action.payload;
      // Add to messages if current chat
      if (state.selectedChat?._id === conversationId) {
        const exists = state.messages.some(m => m._id === message._id);
        if (!exists) {
          state.messages.push(message);
        }
      }
      // Update conversation lastMessage & unreadCount, move to top
      const convoIdx = state.conversations.findIndex(c => c._id === conversationId);
      if (convoIdx > -1) {
        const [convo] = state.conversations.splice(convoIdx, 1);
        convo.lastMessage = {
          text: message.file ? (message.text || 'Sent a file') : message.text,
          senderId: message.sender?._id,
          createdAt: message.createdAt,
        };
        if (state.selectedChat?._id !== conversationId) {
          convo.unreadCount = (convo.unreadCount || 0) + 1;
        }
        state.conversations.unshift(convo);
      }
    },
    setMessagesRead: (state, action) => {
      const chatId = action.payload;
      if (state.selectedChat?._id === chatId) {
        state.messages.forEach(m => { m.isRead = true; });
      }
    },
    removeMessage: (state, action) => {
      const { messageId, chatId } = action.payload;
      if (state.selectedChat?._id === chatId) {
        state.messages = state.messages.filter(m => m._id !== messageId);
      }
    },
    updateUnreadCount: (state, action) => {
      const { chatId, unreadCount } = action.payload;
      const convo = state.conversations.find(c => c._id === chatId);
      if (convo && typeof unreadCount === 'number') {
        convo.unreadCount = unreadCount;
      }
    },
    // Online status
    setOnlineUsers: (state, action) => {
      state.onlineUsers = action.payload;
    },
    addOnlineUser: (state, action) => {
      const userId = action.payload;
      if (!state.onlineUsers.includes(userId)) {
        state.onlineUsers.push(userId);
      }
    },
    removeOnlineUser: (state, action) => {
      state.onlineUsers = state.onlineUsers.filter(id => id !== action.payload);
    },
    // Typing indicators
    setUserTyping: (state, action) => {
      const { chatId, userId } = action.payload;
      state.typingUsers[chatId] = userId;
    },
    clearUserTyping: (state, action) => {
      const { chatId } = action.payload;
      delete state.typingUsers[chatId];
    },
    clearError: (state) => {
      state.error = null;
    },
    setNewConversationPlayer: (state, action) => {
      state.newConversationPlayer = action.payload;
    },
    clearNewConversationPlayer: (state) => {
      state.newConversationPlayer = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchConversations
      .addCase(fetchConversations.pending, (state) => {
        state.loadingConversations = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loadingConversations = false;
        state.conversations = action.payload;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loadingConversations = false;
        state.error = action.payload;
      })
      // fetchMessages
      .addCase(fetchMessages.pending, (state) => {
        state.loadingMessages = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loadingMessages = false;
        state.messages = action.payload.messages;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loadingMessages = false;
        state.error = action.payload;
      })
      // sendMessage
      .addCase(sendMessage.pending, (state) => {
        state.sendingMessage = true;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.sendingMessage = false;
        const msg = action.payload;
        const exists = state.messages.some(m => m._id === msg._id);
        if (!exists) {
          state.messages.push(msg);
        }
        // Update conversation lastMessage and move to top
        const convoIndex = state.conversations.findIndex(c => c._id === msg.conversationId);
        if (convoIndex > -1) {
          const [convo] = state.conversations.splice(convoIndex, 1);
          convo.lastMessage = {
            text: msg.file ? (msg.text || 'Sent a file') : msg.text,
            senderId: msg.sender?._id,
            createdAt: msg.createdAt,
          };
          state.conversations.unshift(convo);
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.sendingMessage = false;
        state.error = action.payload;
      })
      // markAsRead
      .addCase(markAsRead.fulfilled, (state, action) => {
        const convoId = action.payload;
        const convo = state.conversations.find(c => c._id === convoId);
        if (convo) {
          convo.unreadCount = 0;
        }
      })
      // deleteConversations
      .addCase(deleteConversations.fulfilled, (state, action) => {
        const ids = action.payload;
        state.conversations = state.conversations.filter(c => !ids.includes(c._id));
        if (state.selectedChat && ids.includes(state.selectedChat._id)) {
          state.selectedChat = null;
          state.messages = [];
        }
      })
      // deleteMessage
      .addCase(deleteMessage.fulfilled, (state, action) => {
        state.messages = state.messages.filter(m => m._id !== action.payload);
      })
      // archiveConversations
      .addCase(archiveConversations.fulfilled, (state, action) => {
        const ids = action.payload;
        state.conversations = state.conversations.filter(c => !ids.includes(c._id));
        if (state.selectedChat && ids.includes(state.selectedChat._id)) {
          state.selectedChat = null;
          state.messages = [];
        }
      })
      // unarchiveConversations
      .addCase(unarchiveConversations.fulfilled, (state, action) => {
        const ids = action.payload;
        state.conversations = state.conversations.filter(c => !ids.includes(c._id));
      })
      // startConversation
      .addCase(startConversation.pending, (state) => {
        state.startingConversation = true;
        state.error = null;
      })
      .addCase(startConversation.fulfilled, (state, action) => {
        state.startingConversation = false;
        const { conversation, message } = action.payload;
        // Add conversation to list if not exists
        const exists = state.conversations.some(c => c._id === conversation._id);
        if (!exists) {
          state.conversations.unshift(conversation);
        }
        // Set as selected chat
        state.selectedChat = conversation;
        // Add message to messages
        if (message) {
          state.messages = [message];
        }
        // Clear new conversation player
        state.newConversationPlayer = null;
      })
      .addCase(startConversation.rejected, (state, action) => {
        state.startingConversation = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedChat,
  setActiveTab,
  clearMessages,
  addNewMessage,
  setMessagesRead,
  removeMessage,
  updateUnreadCount,
  setOnlineUsers,
  addOnlineUser,
  removeOnlineUser,
  setUserTyping,
  clearUserTyping,
  clearError,
  setNewConversationPlayer,
  clearNewConversationPlayer,
} = messagingSlice.actions;

export default messagingSlice.reducer;
